import { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { ChevronDown, ChevronRight, AlertTriangle, PhoneCall, Mail, Search, Briefcase, Cpu } from "lucide-react";
import { Badge } from "../ui/badge";
import LeadScoreBadge from "./LeadScoreBadge";
import ScoreBreakdown from "./ScoreBreakdown";
import ChannelPriority from "./ChannelPriority";

function getRecommendation(lead) {
  if (lead.dnc) return { text: "Do not contact — DNC flag active", icon: AlertTriangle, color: "text-red-500" };
  if (lead.dne) return { text: "Do not email — DNE flag active", icon: AlertTriangle, color: "text-amber-500" };
  if (!lead.work_direct_phone && !lead.mobile_phone && !lead.work_email && !lead.personal_email)
    return { text: "Research needed — limited contact info", icon: Search, color: "text-blue-500" };
  if (lead.work_direct_phone || lead.mobile_phone) {
    const level = lead.seniority_level === "c_level" ? "C-level" : lead.seniority_level === "vp" ? "VP-level" : "direct phone available";
    return { text: `Call now — ${level}`, icon: PhoneCall, color: "text-green-600" };
  }
  return { text: "Send email — no phone available", icon: Mail, color: "text-amber-600" };
}

export default function ContactStrategyCard({ lead, leadCompany }) {
  const [expanded, setExpanded] = useState(false);

  if (lead.lead_score == null) return null;

  const rec = getRecommendation(lead);
  const RecIcon = rec.icon;

  return (
    <Card className="mb-4">
      <CardContent className="p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <LeadScoreBadge score={lead.lead_score} band={lead.lead_score_band} size="lg" />

          <div className="flex-1 min-w-[200px] flex items-center gap-2 flex-wrap">
            <ChannelPriority lead={lead} bestChannel={lead.best_contact_channel} />
            {/* ATS & AI Voice from company */}
            {leadCompany?.ats_used && (
              <Badge variant="outline" className="text-xs gap-1 bg-orange-50 text-orange-700 border-orange-200">
                <Briefcase className="h-3 w-3" /> ATS: {leadCompany.ats_used}
              </Badge>
            )}
            {leadCompany?.ai_voice_using && (
              <Badge variant="outline" className={`text-xs gap-1 ${leadCompany.ai_voice_using.toLowerCase().includes('jobtalk') ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-300'}`}>
                <Cpu className="h-3 w-3" /> AI Voice: {leadCompany.ai_voice_using}
              </Badge>
            )}
          </div>

          <div className={`flex items-center gap-1.5 text-sm ${rec.color}`}>
            <RecIcon className="h-4 w-4" />
            <span>{rec.text}</span>
          </div>

          <button onClick={() => setExpanded(!expanded)} className="text-muted-foreground hover:text-foreground">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </div>

        {expanded && (
          <div className="mt-4 pt-3 border-t">
            <ScoreBreakdown breakdown={lead.lead_score_breakdown} />
            {lead.reports_to && (
              <p className="text-xs text-muted-foreground mt-2">Reports to: {lead.reports_to}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
